import {
  type CanvasElementFrom,
  type IoConfig,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import { type PropertiesConfigInputColorModel } from '@blaze-shared/elements-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';

import { generateToggleButtonFormField } from './generation/form-field';
import { ToggleButtonComponent } from './toggle-button.component';
import { ToggleButtonPropsBuilder } from './toggle-button.props-builder';
import { getPropsConfig, TOGGLE_BUTTON_SIZE_LIMITS } from './toggle-button.props-config';
import {
  type ToggleButtonPropsModel,
  type ToggleButtonStateStyle,
} from './toggle-button.props-model';

export { transformToToggleButton } from './toggle-button.transforms';
export type ToggleButtonDef = WithProps<ToggleButtonPropsModel> &
  WithIoConfig<'value' | 'visible', 'change'>;
export type ToggleButtonElement = CanvasElementFrom<'toggleButton'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    toggleButton: ToggleButtonDef;
  }
}

const DEFAULT_WIDTH = 168;

registerElementDefinition({
  type: 'toggleButton',
  componentType: ToggleButtonComponent,
  isListed: true,
  name: 'Toggle Button',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: TOGGLE_BUTTON_SIZE_LIMITS.MIN_SIZE },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  propsBuilder: ToggleButtonPropsBuilder,
  generation: {
    formField: generateToggleButtonFormField,
  },
  toolbar: ['triggerClick'],
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  styleExcludePaths: ['style.labelStateView', 'style.backgroundStateView'],
  metaUiFeatures: {
    type: 'input',
    paths: {},
  },
  features: {
    searchAndFiltersColumnTypes: ['Boolean'],
    searchAndFiltersFieldSupport: true,
    preventAutoHeight: true,
  },
  version: 1,
});

function getIoConfig(): IoConfig<'value' | 'visible', 'change'> {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Value', dataType: 'Boolean' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      // the toggle is an input element, so `change` carrying the value is its only output —
      // the default one used by filter connections and forms
      {
        eventType: 'change',
        schema: {
          columns: [
            {
              columnType: 'Boolean',
              dataType: 'Boolean',
              name: '', // for non-object value schema, this should be empty
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'Is Enabled',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'toggleButton'> {
  return {
    content: {
      toggle: {
        defaultState: 'disabled',
        stateView: 'enabled',
        enabled: { label: 'Enabled' },
        disabled: { label: 'Disabled' },
      },
      title: {
        label: 'Toggle Button',
        enabled: false,
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      button: {
        offset: { margin: '$t.spacingInput', padding: '8px' },
      },
      labelStateView: 'enabled',
      backgroundStateView: 'enabled',
      states: {
        enabled: makeStateStyleDefaults(
          { color1: '$t.accentLabel', selectorType: 'solid' },
          { color1: '$t.accent', selectorType: 'solid' },
          { color1: '$t.outline', selectorType: 'none' },
        ),
        disabled: makeStateStyleDefaults(
          { color1: '$t.title', selectorType: 'solid' },
          { color1: '$t.fillInternal', selectorType: 'solid' },
          { color1: '$t.outline', selectorType: 'solid' },
        ),
      },
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
      },
      labelBlock: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight',
        fontStyle: [],
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
    },
  };
}

function makeStateStyleDefaults(
  labelColor: PropertiesConfigInputColorModel,
  fill: PropertiesConfigInputColorModel,
  outline: PropertiesConfigInputColorModel,
): ToggleButtonStateStyle {
  return {
    label: {
      color: labelColor,
      align: 'center',
      iconSize: '$t.buttonIconSize',
    },
    font: {
      style: [],
      fontFamily: '$t.fontFamily',
      fontWeight: '$t.labelFontWeight' as FontWeightOption,
      lineHeight: '$t.lineHeight',
      fontSize: '$t.fontSize',
    },
    background: {
      fill,
      outline,
      outlineWidth: '1px',
      radius: '$t.radius',
    },
  };
}
